import React, {
  useImperativeHandle,
  useState,
  useContext,
  useRef,
  useEffect,
} from 'react'
import styled, { ThemeContext } from 'styled-components'
import Measure, { ContentRect } from 'react-measure'

import ErrorBoundary from 'components/ErrorBoundary'
import Header from 'containers/Header'
import DocumentHead from 'components/DocumentHead'
import { useContainerRefScroll } from 'utils/ContainerRefScrollContext'

interface PageLayoutProps {
  className?: string
  title?: string
  showHeader?: boolean
  header?: React.ReactNode
  footer?: React.ReactNode
  children?:
    | ((props: { headerHeight: number }) => React.ReactNode)
    | React.ReactNode
}

interface PageContentProps {
  headerHeight?: number
}

// Styles
const PageContent = styled.div<PageContentProps>`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  justify-content: space-between;
  margin-top: ${({ headerHeight, theme }) => headerHeight}px;

  /* stylelint-disable declaration-block-no-duplicate-properties */
  padding-left: constant(safe-area-inset-left);
  padding-right: constant(safe-area-inset-right);
  padding-bottom: constant(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
  padding-bottom: env(safe-area-inset-bottom);
  /* stylelint-enable */
`

const Container = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  overflow: auto;

  :focus {
    outline: none;
  }
`

// Components
const FocusedContainer = React.forwardRef<Element, React.PropsWithChildren<{}>>(
  function FocusedContainer(props, ref) {
    const focusRef = useRef<HTMLDivElement>(null)
    const { setContainerRef } = useContainerRefScroll()

    useEffect(() => {
      setContainerRef(focusRef)

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusRef])

    // We are focusing the PageLayout allowing keyboard navigation
    // with arrow keys and page up / page down
    useEffect(() => {
      const isReadyToFocus =
        typeof focusRef.current?.focus === 'function' &&
        document.activeElement?.tagName === 'BODY'

      if (isReadyToFocus) {
        focusRef.current?.focus({ preventScroll: false })
      }
    })

    useImperativeHandle(ref, () => ({
      ...focusRef.current!,
      scrollTo: (...args: any) => {
        focusRef.current?.scrollTo(...args)
      },
    }))

    return <Container {...props} ref={focusRef} tabIndex={-1} />
  }
)

const PageLayout = React.forwardRef<Element, PageLayoutProps>(
  function PageLayout(
    {
      className,
      title,
      header,
      children,
      footer,
      showHeader = true,
      ...props
    }: PageLayoutProps,
    ref
  ) {
    const theme = useContext(ThemeContext)
    const [headerHeight, setHeaderHeight] = useState<number>(
      theme.header.height
    )

    /*
     * Watching header height changes to reposition <Sidebar/>
     * and <PageContent /> when banners are displayed.
     */
    const handleResize = ({ entry }: ContentRect) => {
      if (!entry) {
        return
      }

      // Prevent setting a new state when the value is the same
      if (headerHeight === entry.height) {
        return
      }

      setHeaderHeight(entry.height)
    }

    return (
      <FocusedContainer ref={ref} {...props}>
        <DocumentHead title={title} />
        {showHeader && (
          <Measure onResize={handleResize}>
            {({ measureRef }) => <Header ref={measureRef}>{header}</Header>}
          </Measure>
        )}
        <ErrorBoundary>
          <PageContent headerHeight={headerHeight} className={className}>
            {typeof children === 'function'
              ? children({ headerHeight })
              : children}
          </PageContent>
          {footer}
        </ErrorBoundary>
      </FocusedContainer>
    )
  }
)

export default PageLayout
